import React, { useState } from "react";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { ChevronDown, User, Shield, Lock, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger
} from "@/components/ui/drawer";

interface NavigationLinkProps {
  href: string;
  label: string;
  className?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  requiresAuth?: boolean;
}

const NavigationLink: React.FC<NavigationLinkProps> = ({ 
  href, 
  label, 
  className, 
  onClick, 
  icon, 
  requiresAuth = false 
}) => {
  const { user } = useAuth();
  
  if (requiresAuth && !user) {
    return (
      <NavigationMenuItem>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link 
                to="/auth" 
                className={cn(
                  "px-4 py-2 hover:text-primary hover:bg-primary/5 rounded-md text-muted-foreground group flex items-center gap-2",
                  className
                )}
              >
                {icon && icon}
                {label}
                <Lock className="h-3 w-3 text-muted-foreground" />
              </Link>
            </TooltipTrigger>
            <TooltipContent>
              <p>Sign in required</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </NavigationMenuItem>
    );
  }
  
  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link 
          to={href} 
          className={cn(
            "px-4 py-2 hover:text-primary hover:bg-primary/5 rounded-md text-muted-foreground group flex items-center gap-2",
            className
          )}
          onClick={onClick}
        >
          {icon && icon}
          {label}
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
};

// Mobile Navigation Link for the drawer - make icon optional
interface MobileNavigationLinkProps {
  href: string;
  label: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  requiresAuth?: boolean;
}

const MobileNavigationLink: React.FC<MobileNavigationLinkProps> = ({ 
  href, 
  label, 
  onClick, 
  icon, 
  requiresAuth = false 
}) => {
  const { user } = useAuth();
  
  if (requiresAuth && !user) {
    return (
      <Link 
        to="/auth" 
        className="flex items-center gap-2 px-4 py-3 hover:bg-accent rounded-md w-full"
        onClick={onClick}
      >
        {icon && <span className="w-5 h-5 flex items-center justify-center">{icon}</span>}
        <span>{label}</span>
        <Lock className="h-3 w-3 text-muted-foreground ml-1" />
      </Link>
    );
  }
  
  return (
    <Link 
      to={href} 
      className="flex items-center gap-2 px-4 py-3 hover:bg-accent rounded-md w-full"
      onClick={onClick}
    >
      {icon && <span className="w-5 h-5 flex items-center justify-center">{icon}</span>}
      <span>{label}</span>
    </Link>
  );
};

const Navigation: React.FC = () => {
  const { user, isAdmin, signOut } = useAuth();
  const isMobile = useIsMobile();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const closeDrawer = () => setIsDrawerOpen(false);

  // Mobile Navigation Drawer Component
  const MobileNavigation = () => (
    <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
      <DrawerTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu />
          <span className="sr-only">Menu</span>
        </Button>
      </DrawerTrigger>
      <DrawerContent className="h-[80vh]">
        <div className="p-4 pt-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold">Menu</h2>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </DrawerClose>
          </div>
          
          <div className="space-y-6">
            {/* PIF Insight Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground px-4">PIF Insight</h3>
              <div className="space-y-1">
                <MobileNavigationLink href="/populist-scan" label="Top 20" onClick={closeDrawer} />
                <MobileNavigationLink href="/pif-insight/topics" label="PIF Insight" requiresAuth={true} onClick={closeDrawer} />
              </div>
            </div>
            
            {/* About Us Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground px-4">About Us</h3>
              <div className="space-y-1">
                <MobileNavigationLink href="/about" label="About Us" onClick={closeDrawer} />
                <MobileNavigationLink href="/why-pif" label="Why PIF" onClick={closeDrawer} />
                <MobileNavigationLink href="/populism-score" label="PIF Score" onClick={closeDrawer} />
                <MobileNavigationLink href="/populism-impact" label="Populism Impact" onClick={closeDrawer} />
                <MobileNavigationLink href="/populism-harmful" label="Populism Harmful" onClick={closeDrawer} />
                <MobileNavigationLink href="/political-stand" label="Political Stand" onClick={closeDrawer} />
              </div>
            </div>
            
            {/* User Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground px-4">Account</h3>
              <div className="space-y-1">
                {user ? (
                  <>
                    <MobileNavigationLink href="/profile" label="My Profile" onClick={closeDrawer} />
                    <MobileNavigationLink href="/analyze" label="Analyze Content" onClick={closeDrawer} />
                    {isAdmin && (
                      <MobileNavigationLink 
                        href="/admin" 
                        label="Admin Dashboard" 
                        icon={<Shield className="h-4 w-4" />} 
                        onClick={closeDrawer} 
                      />
                    )}
                    <button 
                      onClick={() => {
                        signOut();
                        closeDrawer();
                      }} 
                      className="flex items-center gap-2 px-4 py-3 hover:bg-accent rounded-md w-full text-left"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <MobileNavigationLink 
                    href="/auth" 
                    label="Sign In" 
                    icon={<User className="h-4 w-4" />} 
                    onClick={closeDrawer} 
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );

  // Desktop Navigation
  const DesktopNavigation = () => (
    <NavigationMenu className="max-w-full flex justify-between overflow-x-auto">
      <NavigationMenuList className="gap-1">
        {/* Left side - could be used for logo or other elements */}
      </NavigationMenuList>
      
      {/* Right side navigation items */}
      <NavigationMenuList className="gap-1 flex items-center">
        {/* PIF Insight dropdown menu */}
        <NavigationMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger className="px-4 py-2 hover:text-primary hover:bg-primary/5 rounded-md text-muted-foreground group flex items-center gap-1">
              PIF Insight <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background border shadow-md">
              <DropdownMenuItem asChild>
                <Link to="/populist-scan" className="w-full cursor-pointer">Top 20</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/pif-insight/topics" className="w-full cursor-pointer flex items-center gap-2">
                  PIF Insight
                  {!user && <Lock className="h-3 w-3 ml-1" />}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </NavigationMenuItem>
        
        {/* About Us dropdown menu */}
        <NavigationMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger className="px-4 py-2 hover:text-primary hover:bg-primary/5 rounded-md text-muted-foreground group flex items-center gap-1">
              About Us <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background border shadow-md">
              <DropdownMenuItem asChild>
                <Link to="/about" className="w-full cursor-pointer">About Us</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/why-pif" className="w-full cursor-pointer">Why PIF</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/populism-score" className="w-full cursor-pointer">PIF Score</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/populism-impact" className="w-full cursor-pointer">Populism Impact</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/populism-harmful" className="w-full cursor-pointer">Populism Harmful</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/political-stand" className="w-full cursor-pointer">Political Stand</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </NavigationMenuItem>
        
        {/* User authentication */}
        {user ? (
          <NavigationMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger className="px-4 py-2 hover:text-primary hover:bg-primary/5 rounded-md text-muted-foreground group flex items-center gap-2">
                <User className="h-4 w-4" />
                My Profile
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-background border shadow-md">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="w-full cursor-pointer">View Profile</Link>
                </DropdownMenuItem>
                
                <DropdownMenuItem asChild>
                  <Link to="/analyze" className="w-full cursor-pointer">Analyze Content</Link>
                </DropdownMenuItem>
                
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="w-full cursor-pointer flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="w-full cursor-pointer">
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </NavigationMenuItem>
        ) : (
          <NavigationMenuItem>
            <Link to="/auth">
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Sign In
              </Button>
            </Link>
          </NavigationMenuItem>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );

  return (
    <div className="flex justify-end items-center">
      {isMobile ? (
        <MobileNavigation />
      ) : (
        <DesktopNavigation />
      )}
    </div>
  );
};

export default Navigation;
